/**
 * Frontend entry point.
 *
 * V1.3.0 wires real AWS Cognito login (replaces v1.2.x's static
 * dev-bypass header path):
 *
 *   - Amplify configured against the RCM Ops user pool
 *   - <Authenticator> renders the login form until the user signs in
 *   - <AuthProvider> exposes decoded ID-token claims to the tree
 *   - <RoleGuard> short-circuits non-RCM_ACCOUNT_MANAGER users with a
 *     full-page access-denied (proactive — they never see app chrome)
 *   - <DevAuthBypassBanner> warns when VITE_AUTH_BYPASS=true is active
 *   - useIdleLogout signs out after VITE_IDLE_MINUTES of inactivity
 *
 * Bypass mode (VITE_AUTH_BYPASS=true) skips the Authenticator entirely
 * and pretends a synthetic AM is logged in. The api/client still sends
 * `Bearer dev-bypass-token`, which the dashboard API's BypassVerifier
 * accepts. Used for local dev when there's no Cognito pool.
 *
 * Env vars (Vite reads VITE_* at build time):
 *   VITE_API_URL                — mips-dashboard-api base URL
 *   VITE_RCM_OPS_POOL_ID        — Cognito user pool ID
 *   VITE_RCM_OPS_CLIENT_ID      — Cognito app client ID
 *   VITE_AUTH_BYPASS            — "true" disables Amplify; mocks AM identity (dev only)
 *   VITE_IDLE_MINUTES           — inactivity logout (default 15)
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";

import { router } from "@/routes";
import { initApiClient } from "@/api/client";
import { AuthProvider } from "@/auth/AuthContext";
import { RoleGuard } from "@/auth/RoleGuard";
import { DevAuthBypassBanner } from "@/auth/DevAuthBypassBanner";
import { useIdleLogout } from "@/auth/useIdleLogout";
import { buildQueryClient } from "@/auth/buildQueryClient";
import { signOut } from "@/auth/tokenProvider";
import "@/lib/taxonomies"; // Register StatusBadge taxonomies once at startup
import "@/styles.css";

const apiUrl = import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8001";
const authBypass = import.meta.env.VITE_AUTH_BYPASS === "true";
const idleMinutes = Number(import.meta.env.VITE_IDLE_MINUTES ?? "15");

if (!authBypass) {
  // Real Cognito auth: configure Amplify before anything else mounts so
  // the Authenticator can read its config when it renders. In bypass
  // mode we never load Amplify at all (saves ~150 KB bundle in dev).
  const { Amplify } = await import("aws-amplify");
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: import.meta.env.VITE_RCM_OPS_POOL_ID,
        userPoolClientId: import.meta.env.VITE_RCM_OPS_CLIENT_ID,
      },
    },
  });
}

initApiClient({ baseURL: apiUrl, authBypass });

const queryClient = buildQueryClient();

/**
 * App shell — runs once we have an authenticated session (or we're in
 * bypass mode). Owns the idle-logout timer and the role gate.
 */
function AppShell() {
  // Idle-logout: 15 min default, configurable via VITE_IDLE_MINUTES.
  // Disabled in bypass mode (no real session to terminate).
  useIdleLogout({
    idleMs: Math.max(1, idleMinutes) * 60_000,
    enabled: !authBypass,
    onIdle: () => {
      console.log("[auth] idle timeout — signing out");
      void signOut({ reload: true });
    },
  });

  return (
    <>
      <DevAuthBypassBanner />
      <RoleGuard>
        <RouterProvider router={router} />
      </RoleGuard>
    </>
  );
}

/**
 * Resolve the root component. In Cognito mode, the Authenticator wraps
 * everything and shows the login form until a session exists. In bypass
 * mode, we skip the Authenticator entirely.
 */
async function renderRoot() {
  const root = ReactDOM.createRoot(document.getElementById("root")!);

  if (authBypass) {
    root.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <AuthProvider bypass={true}>
            <AppShell />
          </AuthProvider>
        </QueryClientProvider>
      </React.StrictMode>,
    );
    return;
  }

  // Real auth: dynamic-import the Amplify UI components so they're not
  // in the bypass-mode bundle.
  const [{ Authenticator }] = await Promise.all([
    import("@aws-amplify/ui-react"),
    import("@aws-amplify/ui-react/styles.css"),
  ]);

  root.render(
    <React.StrictMode>
      <Authenticator hideSignUp>
        {() => (
          <QueryClientProvider client={queryClient}>
            <AuthProvider bypass={false}>
              <AppShell />
            </AuthProvider>
          </QueryClientProvider>
        )}
      </Authenticator>
    </React.StrictMode>,
  );
}

void renderRoot();
